import {
  cancelMDRVaultItemized,
  createMDRVaultItemized,
  getAllDestructMedia,
  getAllDestructMediaTypes,
  getAuthorizationPersonnels,
  getDestructRequestDetail,
  getExistingVaultItemizedMDR,
  getPersonnelAuth,
  getPersonnelSecurity,
  getPossibleMatchesList,
  getUnitOfMeasure,
  getValidateOpenMediaList,
  updateMDRVaultItemized
} from "services/common";
import {
  PROGRAM_ID_VAULT_ITEMIZED,
  SECURE_SYNC_FLAG_Y,
  TAG_TYPES
} from "utils/constants";
import {
  dataDogErrorLog,
  formatArrayAsString,
  getAuthenticatedUserBranch,
  handleRefId
} from "utils/helpers";
import { baseApi } from "../baseApi";
import { CF_URLS } from "../endpoints";

/**
 * @module vaultItemizedApi vault-itemized RTK Query endpoints
 */
export const vaultItemizedApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllPersonnel: builder.query({
      queryFn: async (props) => {
        try {
          const response = await getAuthorizationPersonnels(props);
          response?.sort((a, b) => a?.name.localeCompare(b?.name));
          return {
            data: response?.map(({ personnel_id, name }) => ({
              label: name,
              value: personnel_id
            }))
          };
        } catch (err) {
          const { refId, message } = handleRefId(err);
          dataDogErrorLog(
            message,
            `${process.env.REACT_APP_CF_URL_MODULE_REQUEST}/request/authorizedby`,
            {
              main_district_id: getAuthenticatedUserBranch(),
              customer_id: props.customerId,
              error: err
            },
            refId
          );
          return { error: message };
        }
      },
      providesTags: [TAG_TYPES.customer]
    }),
    getPersonnelAuth: builder.query({
      queryFn: async (props) => {
        try {
          const response = await getPersonnelAuth(props);
          return {
            data: response
          };
        } catch (err) {
          const { refId, message } = handleRefId(err);
          dataDogErrorLog(message, "", { error: err }, refId);
          return { error: message };
        }
      },
      providesTags: [TAG_TYPES.customer]
    }),
    getAllDestructMedia: builder.query({
      queryFn: async (props) => {
        try {
          const response = await getAllDestructMedia(props);
          return {
            data: response
          };
        } catch (err) {
          const { refId, message } = handleRefId(err);
          dataDogErrorLog(message, "", { error: err }, refId);
          return { error: message };
        }
      },
      providesTags: [TAG_TYPES.customer]
    }),
    getUnitOfMeasure: builder.query({
      queryFn: async () => {
        try {
          const response = await getUnitOfMeasure();
          return {
            data: response?.map(({ unit_of_measure_id, display_descr }) => ({
              label: display_descr,
              value: unit_of_measure_id
            }))
          };
        } catch (err) {
          const { refId, message } = handleRefId(err);
          dataDogErrorLog(message, "", { error: err }, refId);
          return { error: message };
        }
      },
      providesTags: [TAG_TYPES.vaultItemized]
    }),
    createMDRVaultItemized: builder.mutation({
      queryFn: async (props) => {
        try {
          const response = await createMDRVaultItemized(props);
          return { data: response };
        } catch (err) {
          const { refId, message } = handleRefId(err);
          dataDogErrorLog(
            message,
            `${process.env.REACT_APP_CF_URL_MODULE_REQUEST}/${CF_URLS.REQUEST_VAULT_ITEMIZED.destruct_request_i_sp}`,
            {
              main_district_id: String(props.mainDistrictId),
              customer_id: String(props.customerId),
              requested_personnel_id: String(props.requestedPersonnelId),
              volser_list: formatArrayAsString(props.volserList),
              im_comment: String(props.imComment),
              business_email_addr: String(props.businessEmailAddr),
              logged_in_employee_id: String(props.loggedInEmployeeId),
              destruct_request_type_id: props.destructRequestTypeId,
              error: err
            },
            refId
          );
          return { error: message };
        }
      },
      providesTags: [TAG_TYPES.vaultItemized]
    }),
    getExistingVaultItemizedMDR: builder.mutation({
      queryFn: async (props) => {
        try {
          const response = await getExistingVaultItemizedMDR(props);
          return { data: response };
        } catch (err) {
          const { refId, message } = handleRefId(err);
          dataDogErrorLog(
            message,
            `${process.env.REACT_APP_CF_URL_MODULE_REQUEST}/${CF_URLS.REQUEST_VAULT_ITEMIZED.destruct_request_detail_l_sp}`,
            {
              main_district_id: props.mainDistrictId,
              destruct_request_id: props.destructRequestId,
              language_locale_id: props.languageLocaleId,
              system_id: props.systemId,
              error: err
            },
            refId
          );
          return { error: message };
        }
      },
      providesTags: [TAG_TYPES.vaultItemized]
    }),
    getPersonnelSecurity: builder.query({
      queryFn: async (props) => {
        try {
          const response = await getPersonnelSecurity(props);
          return { data: response };
        } catch (err) {
          const { refId, message } = handleRefId(err);
          dataDogErrorLog(
            message,
            `${process.env.REACT_APP_CF_URL_MODULE_REQUEST}/${CF_URLS.REQUEST_VAULT_ITEMIZED.check_security_access_sp}`,
            {
              main_district_id: props.mainDistrictId,
              user_login: props.userLogin,
              program_id: PROGRAM_ID_VAULT_ITEMIZED,
              customer_id: props.customerId,
              secure_sync_flag: SECURE_SYNC_FLAG_Y,
              personnel_id: props.personnelId,
              error: err
            },
            refId
          );
          return { error: message };
        }
      },
      providesTags: [TAG_TYPES.vaultItemized]
    }),
    cancelMDRVaultItemized: builder.mutation({
      queryFn: async (props) => {
        try {
          const response = await cancelMDRVaultItemized(props);
          return { data: response };
        } catch (err) {
          return { error: err };
        }
      },
      providesTags: [TAG_TYPES.vaultItemized]
    }),
    updateMDRVaultItemized: builder.mutation({
      queryFn: async (props) => {
        try {
          const response = await updateMDRVaultItemized(props);
          return { data: response };
        } catch (err) {
          const { refId, message } = handleRefId(err);
          dataDogErrorLog(
            message,
            `${process.env.REACT_APP_CF_URL_MODULE_REQUEST}/${CF_URLS.REQUEST_VAULT_ITEMIZED.destruct_request_u2_sp}`,
            {
              main_district_id: props.mainDistrictId,
              destruct_request_id: props.destructRequestId,
              unit_of_measure_id:
                props.unitOfMeasureId === "" ? null : props.unitOfMeasureId,
              im_comment: props.imComment,
              logged_in_employee_id:
                props.loggedInEmployeeId === ""
                  ? null
                  : props.loggedInEmployeeId,
              destruct_req_timestamp: props.destructReqTimestamp,
              bulk_weight: props.weight === "" ? null : props.weight,
              system_id: props.systemId,
              error: err
            },
            refId
          );
          return { error: message };
        }
      },
      providesTags: [TAG_TYPES.vaultItemized]
    }),
    getDestructRequestDetail: builder.query({
      queryFn: async (props) => {
        try {
          const response = await getDestructRequestDetail(props);
          return {
            data: response
          };
        } catch (err) {
          const { refId, message } = handleRefId(err);
          dataDogErrorLog(message, "", { error: err }, refId);
          return { error: message };
        }
      },
      providesTags: [TAG_TYPES.customer]
    }),
    getValidateOpenMediaList: builder.query({
      queryFn: async (props) => {
        try {
          const response = await getValidateOpenMediaList(props);

          return { data: response };
        } catch (err) {
          const { refId, message } = handleRefId(err);
          dataDogErrorLog(
            message,
            `${process.env.REACT_APP_CF_URL_MODULE_REQUEST}/${CF_URLS.REQUEST_VAULT_ITEMIZED.destruct_media_validate_sp}`,
            {
              main_district_id: String(props.mainDistrictId),
              customer_id: String(props.customerId),
              volser_list: formatArrayAsString(props.volserList),
              error: err
            },
            refId
          );
          return { error: message };
        }
      },
      providesTags: [TAG_TYPES.vaultItemized]
    }),
    getPossibleMatchesList: builder.query({
      queryFn: async (props) => {
        try {
          const response = await getPossibleMatchesList(props);

          return { data: response };
        } catch (err) {
          const { refId, message } = handleRefId(err);
          dataDogErrorLog(
            message,
            `${process.env.REACT_APP_CF_URL_MODULE_REQUEST}/${CF_URLS.REQUEST_VAULT_ITEMIZED.destruct_validate_select_media_sp}`,
            {
              main_district_id: String(props.mainDistrictId),
              customer_id: String(props.customerId),
              volser_list: formatArrayAsString(props.volserList),
              selected_volser: props.selectedVolser,
              follow_up: props.followUp,
              error: err
            },
            refId
          );
          return { error: message };
        }
      },
      providesTags: [TAG_TYPES.vaultItemized]
    }),
    getAllDestructMediaTypes: builder.query({
      queryFn: async (props) => {
        try {
          const response = await getAllDestructMediaTypes(props);
          return {
            data: response
          };
        } catch (err) {
          const { refId, message } = handleRefId(err);
          dataDogErrorLog(message, "", { error: err }, refId);
          return { error: message };
        }
      },
      providesTags: [TAG_TYPES.customer]
    })
  })
});
